import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { QueryClient, useQuery, useQueryClient } from "react-query";
import { useFormikContext } from "formik";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { GridRowModel } from "@mui/x-data-grid-pro";
import { Line } from "recharts";

import { ChartFormDetails, ChartSeriesDetails, Item } from "../interfaces";
import { GenericObject } from "../../services/Price";
import { checkErrors, getDefaultOptions } from "../../services/Category";
import { getSelectFilterValues } from "../utils";
import LeadTimeChartFrame, {
  LeadTimeYAxis,
} from "../../Cockpit/charts/LeadTimeChartFrame";
import ChartCard from "../../Cockpit/charts/ChartCard";
import ChartState, { ChartStatus } from "../../Cockpit/charts/ChartState";
import ChartHeaderSelect from "../../Cockpit/charts/ChartHeaderSelect";
import { LEGEND_SX } from "../../Cockpit/charts/constants";
import { createRowMerger, MergedRow } from "../../Cockpit/charts/mergeSeries";
import ChartLegend, { ChartLegendEntry } from "../../components/ChartLegend";
import AppButton from "../../components/AppButton";

const DEFAULT_HEIGHT = 220;
const UNSCOPED = "all";
// A curve is reference data: keep it while the dialog stays open.
const CURVE_STALE_TIME = 5 * 60_000;

const Y_AXIS: LeadTimeYAxis = {
  width: 32,
  domain: [0, 100],
  tickCount: 6,
  tickFormatter: (value) => `${Math.round(value)}%`,
};

// A 404 means "no curve for that scope": an empty series, not a failure.
const fetchCurve = async (url: string): Promise<unknown> => {
  const response = await fetch(url, getDefaultOptions());
  if (response.status === 404) return null;
  checkErrors(response);
  return response.json();
};

// Why a fetched series has nothing to draw: the API had no curve for the
// scope (404) or answered with no point (curve not computed).
type MissingReason = "notFound" | "empty";

interface ChartRows {
  rows: MergedRow[];
  missing: Record<string, MissingReason>;
  leadTime: number;
}

// Every curve is cached on its own url, so changing one series (a new model
// picked) only fetches that one; the merged table is what the chart renders
// and stays referentially stable between renders.
const fetchRows = async (
  queryClient: QueryClient,
  series: ChartSeriesDetails[],
  urls: (string | null)[]
): Promise<ChartRows> => {
  const curves = await Promise.all(
    urls.map((url) =>
      url
        ? queryClient.fetchQuery(["form-chart", url], () => fetchCurve(url), {
            staleTime: CURVE_STALE_TIME,
          })
        : Promise.resolve(null)
    )
  );
  const merger = createRowMerger("leadTime");
  const missing: Record<string, MissingReason> = {};
  let leadTime = 0;
  series.forEach((serie, index) => {
    if (!urls[index]) return;
    const data = curves[index];
    if (data == null) {
      missing[serie.key] = "notFound";
      return;
    }
    const points = serie.points(data);
    // Points that all carry null (corridor rows without a model row) draw
    // nothing either: an empty series, zero being a value.
    if (!points.some((point) => point.value != null)) {
      missing[serie.key] = "empty";
      return;
    }
    points.forEach((point) => {
      merger.row(point.leadTime)[serie.key] = point.value;
      leadTime = Math.min(leadTime, point.leadTime);
    });
  });
  return { rows: merger.rows(), missing, leadTime };
};

/**
 * Read-only lead-time chart fed by the other fields: each series resolves its
 * url from the form values (and the scope picked in the header, when the
 * field declares one) and is redrawn as they change. Never submitted.
 */
const ChartField = ({
  filter,
  row,
}: {
  filter: ChartFormDetails;
  row?: GridRowModel;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { values } = useFormikContext<GenericObject>();
  const [scope, setScope] = useState<number | null>(null);
  // Series switched off from the legend; not remembered beyond the dialog.
  const [hidden, setHidden] = useState<readonly string[]>([]);
  const toggleSeries = useCallback(
    (key: string) =>
      setHidden((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      ),
    []
  );
  const height = filter.height ?? DEFAULT_HEIGHT;
  const chartColors = theme.vars.palette.chart;
  const colorOf = (serie: ChartSeriesDetails) =>
    chartColors[serie.color ?? "model"];

  // Values are a new object on every change: the inputs of the queries go
  // through their serialization so they only react to a real change.
  const scopeParamsKey = JSON.stringify(
    filter.scope ? filter.scope.lookupParams(values, row) : null
  );
  const scopeParams = useMemo<GenericObject | null>(
    () => JSON.parse(scopeParamsKey),
    [scopeParamsKey]
  );
  const scopeApi = filter.scope?.api;
  const scopeQuery = useQuery(
    ["form-chart-scope", scopeApi, scopeParams],
    () => getSelectFilterValues<Item>(scopeApi ?? "", scopeParams ?? undefined),
    { enabled: !!scopeApi && !!scopeParams, staleTime: CURVE_STALE_TIME }
  );
  const scopeOptions = useMemo(
    () =>
      filter.scope && scopeQuery.data
        ? filter.scope.options(scopeQuery.data.results)
        : [],
    [filter.scope, scopeQuery.data]
  );

  const urlsKey = JSON.stringify(
    filter.series.map((serie) => serie.url(values, row, scope))
  );
  const urls = useMemo<(string | null)[]>(() => JSON.parse(urlsKey), [urlsKey]);
  const hasSeries = urls.some((url) => url != null);
  const rowsQuery = useQuery(
    ["form-chart-rows", urls],
    () => fetchRows(queryClient, filter.series, urls),
    { enabled: hasSeries, staleTime: CURVE_STALE_TIME, retry: false }
  );

  const result = rowsQuery.data;
  const status: ChartStatus = !hasSeries
    ? "empty"
    : rowsQuery.isLoading
    ? "loading"
    : rowsQuery.isError
    ? "error"
    : !result || result.rows.length === 0
    ? "empty"
    : "ready";

  const legend = useMemo<ChartLegendEntry[]>(
    () =>
      filter.series
        .filter((_serie, index) => urls[index] != null)
        .map((serie) => ({
          key: serie.key,
          label: intl.formatMessage({ id: serie.label }),
          color: chartColors[serie.color ?? "model"],
          icon: "line",
          dashArray: serie.dashArray,
          lineCap: serie.lineCap,
          muted: result?.missing[serie.key] != null,
          inactive: hidden.includes(serie.key),
        })),
    [filter.series, urls, intl, chartColors, result, hidden]
  );
  // One line per distinct message: two series in the same state share it.
  const missingLabels = [
    ...new Set(
      filter.series.flatMap((serie) => {
        const reason = result?.missing[serie.key];
        const label =
          reason === "notFound" ? serie.notFoundLabel : serie.emptyLabel;
        return reason && label ? [label] : [];
      })
    ),
  ];

  // A failed scope lookup is said, not hidden: the unscoped curve still loads
  // on its own, so nothing else would tell why the selector is gone.
  const scopeSelect =
    filter.scope &&
    (scopeQuery.isError ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {intl.formatMessage({ id: "SOMETHING_WENT_WRONG" })}
        </Typography>
        <AppButton
          intent="secondary"
          size="small"
          onClick={() => scopeQuery.refetch()}
        >
          {intl.formatMessage({ id: "RETRY" })}
        </AppButton>
      </Box>
    ) : scopeOptions.length > 0 ? (
      <ChartHeaderSelect
        value={scope == null ? UNSCOPED : String(scope)}
        options={[
          [UNSCOPED, intl.formatMessage({ id: filter.scope.allLabel })],
          ...scopeOptions.map(
            (option) => [String(option.id), option.label] as const
          ),
        ]}
        onChange={(value) =>
          setScope(value === UNSCOPED ? null : Number(value))
        }
        ariaLabelId={filter.scope.label}
        plainLabels
        dividerAfter={UNSCOPED}
      />
    ) : null);

  // Same card as the cockpit analyse charts, so the curve reads the same way
  // it does on the page behind the dialog.
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
      <ChartCard
        title={intl.formatMessage({ id: filter.label })}
        actions={scopeSelect}
        fullscreen={false}
      >
        <ChartState status={status} height={height} />
        {status === "ready" && result && (
          <LeadTimeChartFrame
            chart="line"
            data={result.rows}
            date={filter.date(values, row)}
            leadTime={result.leadTime}
            yAxis={Y_AXIS}
            height={height}
            syncId={null}
          >
            {filter.series.map((serie, index) =>
              urls[index] != null ? (
                <Line
                  key={serie.key}
                  dataKey={serie.key}
                  type="monotone"
                  stroke={colorOf(serie)}
                  strokeWidth={serie.strokeWidth ?? 2}
                  strokeDasharray={serie.dashArray}
                  strokeLinecap={serie.lineCap}
                  hide={hidden.includes(serie.key)}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ) : null
            )}
          </LeadTimeChartFrame>
        )}
        {legend.length > 0 && (
          <ChartLegend
            entries={legend}
            onEntryClick={toggleSeries}
            sx={LEGEND_SX}
          />
        )}
        {missingLabels.map((label) => (
          <Typography
            key={label}
            variant="caption"
            component="p"
            sx={{ color: "warning.main", textAlign: "center" }}
          >
            {intl.formatMessage({ id: label })}
          </Typography>
        ))}
      </ChartCard>
    </Paper>
  );
};

export default ChartField;
